import type {SafeRailsHTML} from '@github-ui/rails-partial'
import type {Params, PathParam} from '@github-ui/react-router'

import {
  type NonNullableType,
  type QueryDepsFn,
  type QueryRouteQueryConfig,
  QueryRouteQueryType,
} from './data-router-types'
import {queryFnFetch} from './query-fn-fetch'

type RailsQueryDepsFn<RoutePath extends string> = (args: {
  pathname: string
  params: NonNullableType<Params<PathParam<RoutePath>>>
  searchParams: URLSearchParams
}) => {pathname: string}

type RailsPartialQueryOptions = Omit<
  QueryRouteQueryConfig<string, string, string, string, QueryDepsFn<SafeRailsHTML>, SafeRailsHTML, QueryRouteQueryType>,
  'queryDeps' | 'queryFn' | 'queryName'
> & {
  partialName: string
  queryDeps?: RailsQueryDepsFn<SafeRailsHTML>
}

type JSONResponse = {
  payload: Record<string, SafeRailsHTML>
}

type RailsPartialQueryConfig = QueryRouteQueryConfig<
  string,
  string,
  string,
  string,
  RailsQueryDepsFn<SafeRailsHTML>,
  SafeRailsHTML,
  QueryRouteQueryType
>

export function railsPartialQuery({
  partialName,
  queryDeps,
  type,
  ...opts
}: RailsPartialQueryOptions): (routeId: string) => RailsPartialQueryConfig {
  return (routeId: string) => {
    const queryName = `${routeId}.${partialName || 'RailsPartial'}`
    return {
      queryName,
      queryDeps: queryDeps
        ? queryDeps
        : ({pathname}) => {
            return {pathname: `${pathname}/partial`}
          },
      queryFn: async queryKey => {
        const json = await queryFnFetch<JSONResponse>(queryKey)

        const html = json.payload?.[queryName]
        if (!html) {
          throw new Error(`Unable to find payload for query: ${queryName}`)
        }

        return html
      },
      type: type || QueryRouteQueryType.Blocking,
      ...opts,
    }
  }
}
